import { api, apiErrorMessage } from '@bemor/web-kit';
import type { SelectOption } from '@bemor/web-kit/ui';
import { useCallback, useState } from 'react';

import type { Merchant } from '@/api/types';

export const merchantOption = (m: Merchant): SelectOption => ({
    value: m.id,
    label: m.display_name || m.legal_name || m.id,
});

/** Merchants matching `q` (id, legal or display name, descriptor) on the staff merchants list. */
async function searchMerchants(q: string): Promise<SelectOption[]> {
    const page = await api().list<Merchant>('/staff/merchants', {
        params: q ? { q, per_page: 20 } : { per_page: 20 },
    });
    return page.items.map(merchantOption);
}

/**
 * A picker's server search, so every Merchant is reachable and not only the first page. The kit's picker shows a
 * failed search as an empty list, so the failure is kept here for the picker's own alert.
 */
export function useMerchantSearch() {
    const [error, setError] = useState<string | null>(null);
    const [found, setFound] = useState<ReadonlyMap<string, string>>(
        () => new Map(),
    );
    const load = useCallback(async (q: string) => {
        try {
            const options = await searchMerchants(q);
            setError(null);
            setFound((m) => {
                const next = new Map(m);
                for (const o of options) next.set(o.value, o.label);
                return next;
            });
            return options;
        } catch (e) {
            setError(apiErrorMessage(e));
            throw e;
        }
    }, []);
    return { load, error, found };
}

export function SearchError({ error }: { error: string | null }) {
    return error ? (
        <p role="alert" className="text-destructive text-sm">
            The Merchant search failed: {error}
        </p>
    ) : null;
}
